import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  alertErrorClass,
  alertSuccessClass,
  alertInfoClass,
  btnPrimaryClass,
  spinnerClass,
} from "../dashboardStyles";
import { GetPaymentByOrderIdAction } from "../../../store/action";
import {
  PaymentPurpose,
  PaymentStatus,
  formatMoney,
} from "../../../constants/subscription";
import { SMS_CENTER_PATH } from "../../../constants/sms";

/**
 * Where NETOPIA sends the customer back to after the card form.
 *
 * **This page reports; it does not grant.** The URL is one a person can type,
 * so nothing here may activate a subscription — that only happens when NETOPIA
 * tells our server directly, over a signed notification. What this does is read
 * the payment we already have a record of and say where it stands.
 *
 * Which means "pending" is the ordinary first answer, not a failure: the
 * customer's browser usually gets back before the server-to-server notification
 * does. So it polls for a short while before settling on a verdict, and says
 * plainly that the money may already have left even if this screen has not
 * caught up.
 *
 * It lives under /dashboard/billing/ deliberately. Dashboard access is decided
 * by path prefix, so nesting it there inherits the billing tab's permissions
 * and keeps it out of `constants/navigation.js` — a landing page has no business
 * being a sidebar item. The old /dashboard/settings/ address still routes here
 * too, because a payment opened before billing had its own tab was handed that
 * URL and NETOPIA decides when it comes back.
 */

/** How long to keep asking before letting the customer go and look later. */
const POLL_TIMEOUT_MS = 30000;
const POLL_INTERVAL_MS = 2000;

const PaymentReturn = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // NETOPIA appends this to the return URL itself — we never put it there.
  const orderId = searchParams.get("orderId");

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stillPending, setStillPending] = useState(false);

  const startedAt = useRef(Date.now());

  const check = useCallback(async () => {
    if (!orderId) {
      setError(t("Dashboard.Payment.noOrder"));
      setLoading(false);
      return true;
    }

    try {
      const result = await GetPaymentByOrderIdAction(orderId);
      setPayment(result);
      setLoading(false);

      if (result.status !== PaymentStatus.PENDING) return true;

      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setStillPending(true);
        return true;
      }

      return false;
    } catch (err) {
      // A 404 here means the order is not this organization's — someone else's
      // reference, or a typed one. The same answer as any other failure: we
      // cannot tell them anything about it.
      setError(t("Dashboard.Payment.lookupError"));
      setLoading(false);
      return true;
    }
  }, [orderId, t]);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const poll = async () => {
      const done = await check();
      if (cancelled || done) return;
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [check]);

  const isPaid = payment?.status === PaymentStatus.PAID;

  // An SMS package comes back to the same page as a plan payment; what it
  // bought, and where to go next, are what differ.
  const isSmsPackage = payment?.purpose === PaymentPurpose.SMS_PACKAGE;
  const isFailed =
    payment?.status === PaymentStatus.FAILED ||
    payment?.status === PaymentStatus.CANCELED;

  return (
    <div>
      <h1 className={pageTitleClass}>{t("Dashboard.Payment.title")}</h1>
      <p className={pageSubtitleClass}>{t("Dashboard.Payment.subtitle")}</p>

      <div className={`${cardClass} mt-5`}>
        <div className={cardBodyClass}>
          {loading && (
            <div className="flex items-center gap-3 text-ink-500">
              <span className={spinnerClass} aria-hidden="true" />
              <span>{t("Dashboard.Payment.checking")}</span>
            </div>
          )}

          {!loading && error && (
            <div className={alertErrorClass} role="alert">
              {error}
            </div>
          )}

          {!loading && !error && isPaid && (
            <div className={alertSuccessClass} data-testid="payment-paid">
              <i className="bi bi-check-circle mr-1" aria-hidden="true" />
              {t(
                isSmsPackage ? "Dashboard.Payment.sms.paid" : "Dashboard.Payment.paid",
                {
                  amount: formatMoney(
                    payment.amountCents / 100,
                    payment.currency,
                  ),
                },
              )}
            </div>
          )}

          {!loading && !error && isFailed && (
            <div className={alertErrorClass} data-testid="payment-failed" role="alert">
              {t("Dashboard.Payment.failed")}
            </div>
          )}

          {/* Still pending after the polling window. Deliberately not phrased as
              a failure — the money may well have left, and telling someone their
              payment did not work when it did is the worse of the two errors. */}
          {!loading && !error && !isPaid && !isFailed && stillPending && (
            <div className={alertInfoClass} data-testid="payment-pending">
              {t("Dashboard.Payment.stillPending")}
            </div>
          )}

          {payment && (
            <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs font-semibold text-ink-500">
                  {t("Dashboard.Payment.reference")}
                </dt>
                <dd className="m-0 font-mono text-ink-900">{payment.orderId}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-ink-500">
                  {t("Dashboard.Payment.amount")}
                </dt>
                <dd className="m-0 text-ink-900">
                  {formatMoney(payment.amountCents / 100, payment.currency)}
                </dd>
              </div>
            </dl>
          )}

          <div className="mt-5">
            {isSmsPackage ? (
              <Link to={SMS_CENTER_PATH} className={btnPrimaryClass}>
                {t("Dashboard.Payment.sms.backToSms")}
              </Link>
            ) : (
              <Link to="/dashboard/billing" className={btnPrimaryClass}>
                {t("Dashboard.Payment.backToPlan")}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReturn;
